import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Pause, Play, Save, CalendarClock, MessageCircle, Youtube, ChevronDown } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

const inputCls = "w-full rounded-2xl border-2 border-[#E5DFC8] focus:border-hv-teal bg-hv-cream px-4 py-2.5 font-semibold outline-none";
const labelCls = "block text-sm font-semibold text-hv-slate mb-1";

function Section({ icon: Icon, title, headerExtra, children }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <section className="rounded-3xl bg-white border border-[#E5DFC8] p-6">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => setCollapsed((c) => !c)} className="flex items-center gap-2 group">
          <Icon className="w-5 h-5 text-hv-coral" strokeWidth={2.5} />
          <h2 className="hv-heading text-xl">{title}</h2>
          <ChevronDown className={`w-4 h-4 text-hv-slate transition-transform duration-200 ${collapsed ? "-rotate-90" : ""} group-hover:text-hv-navy`} strokeWidth={2.5} />
        </button>
        {headerExtra}
      </div>
      {!collapsed && <div className="mt-4 space-y-4">{children}</div>}
    </section>
  );
}

export default function AdminReleaseManager() {
  const [release, setRelease] = useState(null);
  const [support, setSupport] = useState({ enabled: false, whatsapp_number: "", message_template: "" });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, sp] = await Promise.all([
        client.get("/admin/settings/release"),
        client.get("/admin/settings/support"),
      ]);
      setRelease(r.data);
      setSupport(sp.data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveRelease = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      const { data } = await client.put("/admin/settings/release", {
        cycle_days: Number(release.cycle_days),
        live_day: Number(release.live_day),
        beta_mode: !!release.beta_mode,
      });
      setRelease(data);
      setMsg("Release settings saved.");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const togglePause = async () => {
    setErr(""); setMsg("");
    try {
      if (release.paused) {
        await client.post("/admin/settings/release/resume");
        setMsg("Resumed — every learner's clock picks up where it left off.");
      } else {
        await client.post("/admin/settings/release/pause");
        setMsg("Paused — no learner's clock will advance until resumed.");
      }
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  const saveSupport = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    try {
      const { data } = await client.put("/admin/settings/support", support);
      setSupport(data);
      setMsg("Support settings saved.");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  return (
    <div data-testid="admin-release-manager">
      <AdminPageHeader icon={CalendarClock} title="Weekly Release Manager" subtitle="Pacing and pause/resume" />

      {err && <div className="mb-4 text-hv-coral font-bold text-sm" data-testid="release-error">{err}</div>}
      {msg && <div className="mb-4 text-hv-teal font-bold text-sm" data-testid="release-msg">{msg}</div>}

      {loading || !release ? (
        <p className="text-hv-slate text-sm">Loading…</p>
      ) : (
        <div className="space-y-8">
          <Section
            icon={CalendarClock}
            title="Schedule defaults"
            headerExtra={
              <button onClick={togglePause} data-testid="release-pause-toggle"
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 hv-heading text-sm ${release.paused ? "bg-hv-teal text-white" : "bg-hv-coral text-white"}`}>
                {release.paused ? <><Play size={16} /> Resume</> : <><Pause size={16} /> Pause</>}
              </button>
            }
          >
            {release.paused && <p className="text-sm text-hv-coral font-bold">Paused — no learner's clock is advancing right now.</p>}
            <form onSubmit={saveRelease} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Cycle length (days)</label>
                  <input type="number" min="2" max="31" value={release.cycle_days}
                         onChange={(e) => setRelease((r) => ({ ...r, cycle_days: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Live day (1-cycle length)</label>
                  <input type="number" min="1" max="31" value={release.live_day}
                         onChange={(e) => setRelease((r) => ({ ...r, live_day: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <label className="flex items-center gap-3 rounded-2xl bg-hv-navyTint px-4 py-3 cursor-pointer max-w-full">
                <input type="checkbox" checked={!!release.beta_mode} className="w-5 h-5 accent-hv-coral"
                       onChange={(e) => setRelease((r) => ({ ...r, beta_mode: e.target.checked }))} />
                <span className="font-semibold text-hv-navy">Beta mode — unlock everything for every learner (testers only)</span>
              </label>
              <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-hv-gold px-5 py-2.5 hv-heading text-hv-navy">
                <Save size={16} /> Save pacing
              </button>
            </form>
          </Section>

          <div className="rounded-3xl bg-white border border-[#E5DFC8] p-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Youtube className="w-5 h-5 text-hv-coral" strokeWidth={2.5} />
              <div>
                <h2 className="hv-heading text-xl">Weekly recap links</h2>
                <p className="text-sm text-hv-slate mt-0.5">Every learner is on their own week — set each week's recap link separately.</p>
              </div>
            </div>
            <Link to="/admin/weekly-recaps" className="inline-flex items-center gap-2 rounded-full bg-hv-gold px-5 py-2.5 hv-heading text-hv-navy">
              Manage weekly recaps
            </Link>
          </div>

          <Section icon={MessageCircle} title="WhatsApp support">
            <form onSubmit={saveSupport} className="space-y-4">
              <label className="flex items-center gap-3 rounded-2xl bg-hv-cream px-4 py-3 cursor-pointer max-w-full">
                <input type="checkbox" checked={!!support.enabled} className="w-5 h-5 accent-hv-coral"
                       onChange={(e) => setSupport((s) => ({ ...s, enabled: e.target.checked }))} />
                <span className="font-semibold text-hv-navy">Show the WhatsApp support button</span>
              </label>
              <div>
                <label className={labelCls}>WhatsApp number</label>
                <input placeholder="+15551234567" value={support.whatsapp_number || ""}
                       onChange={(e) => setSupport((s) => ({ ...s, whatsapp_number: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Prefilled message template</label>
                <input value={support.message_template || ""} onChange={(e) => setSupport((s) => ({ ...s, message_template: e.target.value }))} className={inputCls} />
              </div>
              <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-hv-gold px-5 py-2.5 hv-heading text-hv-navy"><Save size={16} /> Save</button>
            </form>
          </Section>
        </div>
      )}
    </div>
  );
}
